import {
  compact,
  filter,
  find,
  get,
  isArray,
  isNil,
  size,
  some,
  split,
  trim,
  values,
} from 'lodash';

import { useScenarioProductsStore } from '@/stores/scenario-products';

const moveProductToNewSpacebreak = async function (
  changedDataElement,
  spacebreakId,
  {
    updateAssortmentCanvasProductById,
    updateCanvasProduct,
    showError,
    spacebreaksOrderedObject,
    getAssociatedPallets,
    getIndexedCanvasProducts,
  },
  message
) {
  if (changedDataElement.currentSpacebreakId === spacebreakId) {
    updateAssortmentCanvasProductById(changedDataElement);
    return;
  }

  // If using pallets make sure that russian doll is respected
  const newSpacebreak = spacebreaksOrderedObject[spacebreakId];
  const associatedPallets = await getAssociatedPallets({
    productKeyDisplay: changedDataElement.productKeyDisplay,
  });
  // Map the pallets to the needed update to clear spacebreaks
  const palletUpdates = associatedPallets.reduce((result, pallet) => {
    // Currently only the scenario product for pallet is used - need to grab the canvas product for pallet
    const canvasPallet = getIndexedCanvasProducts[pallet.productKey];
    const palletSpacebreakId = canvasPallet.currentSpacebreakId;
    // No need to clear if the currentSpacebreakId is already null
    if (palletSpacebreakId) {
      const palletSpacebreak = spacebreaksOrderedObject[palletSpacebreakId];
      // Clear the pallet sb if it is smaller than the updated SB to respect the russian doll model
      if (palletSpacebreak.size < newSpacebreak.size) {
        result.push(
          updateCanvasProduct({
            _id: canvasPallet._id,
            currentSpacebreakId: null,
            locallyUpdatedProduct: {
              ...canvasPallet,
              currentSpacebreakId: null,
            },
          })
        );
      }
    }
    return result;
  }, []);

  // If moving to a smaller spacebreak - then check if pallets need have sb cleared
  if (palletUpdates.length) {
    // Clear the pallets & notify the user
    await Promise.all(palletUpdates);
    // Show Snackbar with message that pallets sb have been cleared
    showError(message);
  }

  // locallyUpdatedProduct is meant to be used before the changes are done on the server,
  // to avoid product dragging flickering
  const locallyUpdatedProduct = {
    ...changedDataElement,
    currentSpacebreakId: spacebreakId,
  };
  await updateCanvasProduct({
    _id: changedDataElement._id,
    currentSpacebreakId: spacebreakId,
    locallyUpdatedProduct,
  });
};

const selectedSpacebreak = function (spacebreaksShortNames, currentSpacebreakId) {
  return find(spacebreaksShortNames, { value: currentSpacebreakId });
};

const futureProductIdString = function (kpku, futureId) {
  const identifiers = compact([kpku, futureId].filter(el => !isNil(el)));
  if (!identifiers.length) return '-';
  return identifiers.length > 1 ? identifiers.join('/') : identifiers.toString();
};

function getImageUrl(clientConfig, product) {
  // First, get a corresponding scenario product, as it contains all possible identifiers
  const scenarioProductsStore = useScenarioProductsStore();
  const scenarioProduct = scenarioProductsStore.getScenarioProductStateByProductKey(
    product.productKey
  );
  if (!scenarioProduct) {
    // Fallback to a generic image or empty
    return '';
  }

  const { extension, fieldIdentifier = 'productKeyDisplay' } = clientConfig.images;
  const internalSourceEnabled = get(clientConfig, 'images.internalSourceEnabled', true);

  const endpoint = internalSourceEnabled ? '/api/products/image' : clientConfig.images.cdnImagesUrl;

  const filename = scenarioProduct[fieldIdentifier] || scenarioProduct.productKeyDisplay;

  return `${endpoint}/${filename}${extension}`;
}

/**
 * Searches for a product by its key display or description, case insensitive.
 * @param {string} searchTerm - The search string to look for.
 * @param {string} productKeyDisplay - The product key display.
 * @param {string} itemDescription - The item description.
 * @returns {boolean} True if the search string is found in either the product key display or item description, false otherwise.
 */
function searchProductByKeyOrDescription(searchTerm, productKeyDisplay, itemDescription) {
  if (!searchTerm) return true;

  const multiSelectSeparators = {
    // if you match on a character, also match on it + whitespace
    // e.g. a comma should match a single comma and a comma either preceded or succeeded by arbitrary amount of whitespace
    commaAndSpaces: /\s*,\s*/,
    // Row and column copy-paste from Excel
    // Row-copy only gives us single spaces. Column gives a tab character, which is also matched by the whitespace regex.
    // Both ultimately only give spaces in searchbox so we split *only* when digits surround them
    spaces: /\s+/,
  };

  // Trimming leading and terminal whitespace simplifies the handling.
  searchTerm = trim(searchTerm);

  // If commas are present, don't split string - this should be matched exactly
  const termSurroundedByQuotes = /^["'].*["']$/.test(searchTerm);

  if (!termSurroundedByQuotes) {
    // Try multiple separators in order. Only one can be applied - stop once a split is successful
    some(values(multiSelectSeparators), matchPattern => {
      const multiSplit = split(searchTerm, matchPattern);
      if (size(multiSplit) > 1) {
        searchTerm = split(searchTerm, matchPattern);
        return true;
      }
    });
  } else {
    // Quotes are present - remove them
    searchTerm = searchTerm.replaceAll(/["']/g, '');
  }

  // We always compare against an array, ensure the term is one
  if (!isArray(searchTerm)) searchTerm = [searchTerm];
  // Single commas like `123, ` can produce empty string search terms which match all products. Remove these.
  searchTerm = filter(searchTerm);

  const isInProductDisplayKey = some(searchTerm, part =>
    String(productKeyDisplay).toLowerCase().includes(part.toLowerCase())
  );
  const isInDescription = some(searchTerm, part =>
    new RegExp(part, 'i').test(itemDescription || '')
  );

  return isInProductDisplayKey || isInDescription;
}

export default {
  moveProductToNewSpacebreak,
  selectedSpacebreak,
  futureProductIdString,
  getImageUrl,
  searchProductByKeyOrDescription,
};
